import { useCallback, useEffect, useState } from 'react'
import {
  readFunctionalStorage,
  writeFunctionalStorage,
} from '@/utils/privacyPreferences'
import {
  ADMIN_CURRENT_ENV_STORAGE_KEY,
  type NetworkEnvironment,
  withNetworkStorageKey,
} from '@/utils/storageKeys'

export type { NetworkEnvironment }

const normalizeEnv = (value?: string | null): NetworkEnvironment | null => {
  if (!value) {
    return null
  }
  const normalized = value.trim().toLowerCase()
  if (normalized === 'mainnet' || normalized === 'testnet') {
    return normalized
  }
  return null
}

export const resolveDefaultNetworkEnv = (): NetworkEnvironment => {
  const rawValue = (import.meta.env as { VITE_ADMIN_DEFAULT_NETWORK?: string })
    .VITE_ADMIN_DEFAULT_NETWORK
  const raw = normalizeEnv(rawValue)
  if (!raw) {
    throw new Error(
      'VITE_ADMIN_DEFAULT_NETWORK must be set to mainnet or testnet'
    )
  }
  return raw
}

export const readStoredNetworkEnv = (): NetworkEnvironment => {
  if (typeof window === 'undefined') {
    return resolveDefaultNetworkEnv()
  }

  if (window.currentEnv === 'mainnet' || window.currentEnv === 'testnet') {
    return window.currentEnv
  }

  const stored = normalizeEnv(readFunctionalStorage(ADMIN_CURRENT_ENV_STORAGE_KEY))
  if (stored) {
    window.currentEnv = stored
    return stored
  }

  const fallback = resolveDefaultNetworkEnv()
  writeFunctionalStorage(ADMIN_CURRENT_ENV_STORAGE_KEY, fallback)
  window.currentEnv = fallback
  return fallback
}

export const writeStoredNetworkEnv = (env: NetworkEnvironment): void => {
  if (typeof window === 'undefined') {
    return
  }
  writeFunctionalStorage(ADMIN_CURRENT_ENV_STORAGE_KEY, env)
  window.currentEnv = env
}

export const getCurrentNetworkEnv = (): NetworkEnvironment => readStoredNetworkEnv()

export const getNetworkStorageKey = (
  baseKey: string,
  mode?: NetworkEnvironment
): string => {
  const resolved = mode ?? getCurrentNetworkEnv()
  return withNetworkStorageKey(baseKey, resolved)
}

const broadcastEnvChange = (env: NetworkEnvironment) => {
  if (typeof window === 'undefined') {
    return
  }

  writeStoredNetworkEnv(env)
  window.dispatchEvent(
    new CustomEvent<NetworkEnvironment>('currentEnv-changed', { detail: env })
  )
}

export const useNetworkEnvironment = () => {
  const [currentEnv, setCurrentEnv] = useState<NetworkEnvironment>(() =>
    readStoredNetworkEnv()
  )

  const setNetworkEnvironment = useCallback((nextEnv: NetworkEnvironment) => {
    setCurrentEnv(nextEnv)
    broadcastEnvChange(nextEnv)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleStorage = () => {
      const stored = normalizeEnv(readFunctionalStorage(ADMIN_CURRENT_ENV_STORAGE_KEY))
      if (stored) {
        window.currentEnv = stored
        setCurrentEnv(stored)
      } else if (window.currentEnv === 'mainnet' || window.currentEnv === 'testnet') {
        setCurrentEnv(window.currentEnv)
      }
    }

    const handleCustomChange = (event: Event) => {
      const detail = (event as CustomEvent<NetworkEnvironment>).detail
      if (detail === 'mainnet' || detail === 'testnet') {
        setCurrentEnv(detail)
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(
      'currentEnv-changed',
      handleCustomChange as EventListener
    )

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(
        'currentEnv-changed',
        handleCustomChange as EventListener
      )
    }
  }, [])

  return { currentEnv, setNetworkEnvironment }
}
